<script setup lang="ts">
import {
  ChevronLeft,
  ChevronRight,
  Expand,
  ImageOff,
  Minimize2,
  ZoomIn,
  ZoomOut,
} from "lucide-vue-next";

const {
  images,
  alt,
  title,
  price,
  initialIndex = 0,
  fullscreen = false,
} = defineProps<{
  images: string[];
  alt: string;
  title: string;
  price: string;
  initialIndex?: number;
  fullscreen?: boolean;
}>();

const emit = defineEmits<{
  "update:fullscreen": [value: boolean];
}>();

type PointerGesture =
  | {
      mode: "swipe";
      pointerId: number;
      startX: number;
    }
  | {
      mode: "pan";
      pointerId: number;
      startX: number;
      startY: number;
      initialPanX: number;
      initialPanY: number;
    };

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

const viewport = ref<HTMLElement | null>(null);
const currentIndex = ref(0);
const failedImages = ref(new Set<string>());
const pointerGesture = ref<PointerGesture | null>(null);
const ignoreNextClick = ref(false);
const isSliding = ref(false);
const slideDirection = ref<"next" | "previous">("next");
const zoom = ref(MIN_ZOOM);
const panX = ref(0);
const panY = ref(0);
const isPanning = ref(false);

let slideFallbackTimer: ReturnType<typeof setTimeout> | null = null;

const isFullscreen = computed({
  get: () => fullscreen,
  set: value => emit("update:fullscreen", value),
});

const availableImages = computed(() =>
  images.filter(
    (image): image is string =>
      Boolean(image) && !failedImages.value.has(image),
  ),
);

const currentImage = computed(
  () => availableImages.value[currentIndex.value] ?? null,
);

const hasMultipleImages = computed(() => availableImages.value.length > 1);

const slideTransitionName = computed(() =>
  slideDirection.value === "next"
    ? "details-slide-next"
    : "details-slide-previous",
);

const imageTransform = computed(
  () => `translate3d(${panX.value}px, ${panY.value}px, 0) scale(${zoom.value})`,
);

const imageCursorClass = computed(() => {
  if (!isFullscreen.value) return "cursor-pointer";

  if (zoom.value > MIN_ZOOM) {
    return isPanning.value ? "cursor-grabbing" : "cursor-grab";
  }

  return "cursor-pointer";
});

watch(
  () => initialIndex,
  index => {
    currentIndex.value = Math.min(
      Math.max(index, 0),
      Math.max(availableImages.value.length - 1, 0),
    );
    resetView();
  },
  { immediate: true },
);

watch(availableImages, images => {
  finishSlide();

  if (!images.length) {
    currentIndex.value = 0;
    resetView();
    return;
  }

  if (currentIndex.value >= images.length) {
    currentIndex.value = images.length - 1;
    resetView();
  }
});

watch(isFullscreen, fullscreen => {
  if (!fullscreen) {
    resetView();
  }
});

function clearSlideFallback() {
  if (!slideFallbackTimer) return;

  clearTimeout(slideFallbackTimer);
  slideFallbackTimer = null;
}

function finishSlide() {
  clearSlideFallback();
  isSliding.value = false;
}

function resetView() {
  zoom.value = MIN_ZOOM;
  panX.value = 0;
  panY.value = 0;
  isPanning.value = false;
  pointerGesture.value = null;
}

function changePhoto(targetIndex: number, direction: "next" | "previous") {
  if (
    !hasMultipleImages.value ||
    isSliding.value ||
    targetIndex === currentIndex.value
  ) {
    return;
  }

  resetView();
  slideDirection.value = direction;
  isSliding.value = true;
  currentIndex.value = targetIndex;
  slideFallbackTimer = setTimeout(finishSlide, 380);
}

function previousPhoto() {
  if (!hasMultipleImages.value) return;

  const targetIndex =
    currentIndex.value === 0
      ? availableImages.value.length - 1
      : currentIndex.value - 1;

  changePhoto(targetIndex, "previous");
}

function nextPhoto() {
  if (!hasMultipleImages.value) return;

  const targetIndex =
    currentIndex.value === availableImages.value.length - 1
      ? 0
      : currentIndex.value + 1;

  changePhoto(targetIndex, "next");
}

function selectPhoto(index: number) {
  if (index === currentIndex.value) return;

  changePhoto(index, index > currentIndex.value ? "next" : "previous");
}

function openFullscreen() {
  if (!currentImage.value) return;

  if (ignoreNextClick.value) {
    ignoreNextClick.value = false;
    return;
  }

  isFullscreen.value = true;
}

function handleImageClick() {
  if (!currentImage.value) return;

  if (ignoreNextClick.value) {
    ignoreNextClick.value = false;
    return;
  }

  if (isFullscreen.value) {
    toggleZoom();
  } else {
    openFullscreen();
  }
}

function closeFullscreen() {
  isFullscreen.value = false;
}

function setZoom(nextZoom: number) {
  zoom.value = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextZoom));

  if (zoom.value === MIN_ZOOM) {
    panX.value = 0;
    panY.value = 0;
    return;
  }

  clampPan();
}

function zoomIn() {
  setZoom(zoom.value + ZOOM_STEP);
}

function zoomOut() {
  setZoom(zoom.value - ZOOM_STEP);
}

function toggleZoom() {
  if (!isFullscreen.value) return;

  setZoom(zoom.value === MIN_ZOOM ? 2 : MIN_ZOOM);
}

function clampPan() {
  const element = viewport.value;

  if (!element || zoom.value <= MIN_ZOOM) {
    panX.value = 0;
    panY.value = 0;
    return;
  }

  const maxPanX = (element.clientWidth * (zoom.value - 1)) / 2;
  const maxPanY = (element.clientHeight * (zoom.value - 1)) / 2;

  panX.value = Math.min(maxPanX, Math.max(-maxPanX, panX.value));
  panY.value = Math.min(maxPanY, Math.max(-maxPanY, panY.value));
}

function handleWheel(event: WheelEvent) {
  if (!isFullscreen.value || !currentImage.value) return;

  event.preventDefault();

  if (event.deltaY < 0) {
    zoomIn();
  } else {
    zoomOut();
  }
}

function markAsFailed(image: string) {
  const next = new Set(failedImages.value);
  next.add(image);
  failedImages.value = next;
}

function handlePointerDown(event: PointerEvent) {
  if (event.pointerType === "mouse" && event.button !== 0) return;

  const element = event.currentTarget as HTMLElement;

  if (isFullscreen.value && zoom.value > MIN_ZOOM) {
    pointerGesture.value = {
      mode: "pan",
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      initialPanX: panX.value,
      initialPanY: panY.value,
    };
    isPanning.value = true;
    element.setPointerCapture?.(event.pointerId);
    event.preventDefault();
    return;
  }

  if (event.pointerType !== "mouse") {
    pointerGesture.value = {
      mode: "swipe",
      pointerId: event.pointerId,
      startX: event.clientX,
    };
  }
}

function handlePointerMove(event: PointerEvent) {
  const gesture = pointerGesture.value;

  if (
    !gesture ||
    gesture.mode !== "pan" ||
    gesture.pointerId !== event.pointerId
  ) {
    return;
  }

  panX.value = gesture.initialPanX + event.clientX - gesture.startX;
  panY.value = gesture.initialPanY + event.clientY - gesture.startY;
  clampPan();
  event.preventDefault();
}

function handlePointerUp(event: PointerEvent) {
  const gesture = pointerGesture.value;

  if (!gesture || gesture.pointerId !== event.pointerId) return;

  pointerGesture.value = null;

  const element = event.currentTarget as HTMLElement;
  element.releasePointerCapture?.(event.pointerId);

  if (gesture.mode === "pan") {
    isPanning.value = false;
    ignoreNextClick.value = true;
    return;
  }

  const distance = event.clientX - gesture.startX;

  if (Math.abs(distance) < 45) return;

  ignoreNextClick.value = true;

  if (distance > 0) {
    previousPhoto();
  } else {
    nextPhoto();
  }
}

function cancelPointerGesture() {
  pointerGesture.value = null;
  isPanning.value = false;
}

onBeforeUnmount(() => {
  clearSlideFallback();
});

defineExpose({
  previousPhoto,
  nextPhoto,
  zoomIn,
  zoomOut,
  resetView,
});
</script>

<template>
  <Teleport to="body" :disabled="!isFullscreen">
    <div
      class="min-h-0 bg-black"
      :class="
        isFullscreen
          ? 'fixed inset-0 z-[5200] flex flex-col'
          : 'flex h-full flex-col'
      "
      @click.stop
    >
      <div
        ref="viewport"
        data-service-gallery-viewport
        class="group relative min-h-0 flex-1 select-none overflow-hidden bg-black"
        :class="isFullscreen && zoom > 1 ? 'touch-none' : 'touch-pan-y'"
        @pointerdown="handlePointerDown"
        @pointermove="handlePointerMove"
        @pointerup="handlePointerUp"
        @pointercancel="cancelPointerGesture"
        @wheel="handleWheel"
      >
        <button
          v-if="currentImage"
          type="button"
          class="absolute inset-0 overflow-hidden text-left"
          :class="imageCursorClass"
          :aria-label="
            isFullscreen
              ? zoom > 1
                ? $t('worker_profile.reset_zoom')
                : $t('worker_profile.zoom_in')
              : $t('worker_profile.expand_photo')
          "
          @click="handleImageClick"
        >
          <Transition
            :name="slideTransitionName"
            @after-enter="finishSlide"
            @enter-cancelled="finishSlide"
          >
            <div :key="currentImage" class="absolute inset-0">
              <img
                :src="currentImage"
                :alt="hasMultipleImages ? `${alt} ${currentIndex + 1}` : alt"
                draggable="false"
                class="size-full object-contain transition-transform duration-150 will-change-transform"
                :style="{ transform: imageTransform }"
                @dragstart.prevent
                @error="markAsFailed(currentImage)"
              />
            </div>
          </Transition>
        </button>

        <div
          v-else
          class="absolute inset-0 flex items-center justify-center text-center text-white/65"
        >
          <div>
            <ImageOff class="mx-auto size-9" />
            <p class="mt-3 text-sm font-semibold">
              {{ $t("worker_profile.no_service_photos") }}
            </p>
          </div>
        </div>

        <template v-if="hasMultipleImages">
          <button
            type="button"
            class="absolute left-3 top-1/2 z-20 flex size-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-default disabled:opacity-45 sm:left-4"
            :aria-label="$t('worker_profile.previous_photo')"
            :disabled="isSliding"
            @pointerdown.stop
            @pointerup.stop
            @click.stop="previousPhoto"
          >
            <ChevronLeft :class="isFullscreen ? 'size-7' : 'size-6'" />
          </button>

          <button
            type="button"
            class="absolute right-3 top-1/2 z-20 flex size-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-default disabled:opacity-45 sm:right-4"
            :aria-label="$t('worker_profile.next_photo')"
            :disabled="isSliding"
            @pointerdown.stop
            @pointerup.stop
            @click.stop="nextPhoto"
          >
            <ChevronRight :class="isFullscreen ? 'size-7' : 'size-6'" />
          </button>
        </template>

        <div
          v-if="isFullscreen && currentImage"
          class="pointer-events-none absolute bottom-4 left-5 z-30 hidden max-w-[min(460px,calc(100vw-360px))] rounded-2xl bg-black/65 px-5 py-4 text-white shadow-lg backdrop-blur-md lg:block"
        >
          <p
            class="text-xs font-bold uppercase tracking-[0.12em] text-white/60"
          >
            {{ $t("worker_profile.service_details") }}
          </p>

          <h2 class="mt-1.5 break-words text-xl font-bold leading-snug">
            {{ title }}
          </h2>

          <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <strong class="break-words text-base font-bold text-white">
              {{ price }}
            </strong>

            <span
              v-if="availableImages.length"
              class="text-sm font-semibold text-white/65"
            >
              {{ currentIndex + 1 }} / {{ availableImages.length }}
            </span>
          </div>
        </div>

        <span
          v-else-if="availableImages.length"
          class="pointer-events-none absolute bottom-3 left-3 z-20 rounded-lg bg-black/65 px-3 py-1.5 text-sm font-semibold text-white backdrop-blur-sm"
        >
          {{ currentIndex + 1 }} / {{ availableImages.length }}
        </span>

        <button
          v-if="currentImage"
          type="button"
          class="absolute z-30 flex size-10 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          :class="isFullscreen ? 'right-4 top-4 size-11' : 'bottom-3 right-3'"
          :aria-label="
            isFullscreen
              ? $t('worker_profile.collapse_photo')
              : $t('worker_profile.expand_photo')
          "
          @pointerdown.stop
          @pointerup.stop
          @click.stop="isFullscreen ? closeFullscreen() : openFullscreen()"
        >
          <Minimize2 v-if="isFullscreen" class="size-5" />
          <Expand v-else class="size-5" />
        </button>

        <div
          v-if="isFullscreen && currentImage"
          class="absolute bottom-4 right-5 z-30 hidden items-center gap-1 rounded-2xl bg-black/65 p-1.5 text-white shadow-lg backdrop-blur-md lg:flex"
          @pointerdown.stop
          @pointerup.stop
          @click.stop
        >
          <button
            type="button"
            class="flex size-10 cursor-pointer items-center justify-center rounded-xl transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-35"
            :disabled="zoom <= 1"
            :aria-label="$t('worker_profile.zoom_out')"
            @click="zoomOut"
          >
            <ZoomOut class="size-5" />
          </button>

          <button
            type="button"
            class="min-w-16 cursor-pointer rounded-xl px-2 py-2 text-sm font-semibold transition hover:bg-white/15"
            :aria-label="$t('worker_profile.reset_zoom')"
            @click="resetView"
          >
            {{ Math.round(zoom * 100) }}%
          </button>

          <button
            type="button"
            class="flex size-10 cursor-pointer items-center justify-center rounded-xl transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-35"
            :disabled="zoom >= 4"
            :aria-label="$t('worker_profile.zoom_in')"
            @click="zoomIn"
          >
            <ZoomIn class="size-5" />
          </button>
        </div>
      </div>

      <div
        v-if="isFullscreen && currentImage"
        class="shrink-0 border-t border-white/10 bg-black px-4 py-3 text-white lg:hidden"
      >
        <p class="text-xs font-bold uppercase tracking-[0.12em] text-white/55">
          {{ $t("worker_profile.service_details") }}
        </p>

        <h2 class="mt-1 break-words text-lg font-bold leading-snug">
          {{ title }}
        </h2>

        <div
          class="mt-2 flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-3"
        >
          <div class="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <strong
              class="min-w-0 break-words text-sm font-bold text-white sm:text-base"
            >
              {{ price }}
            </strong>

            <span
              v-if="availableImages.length"
              class="shrink-0 text-xs font-semibold text-white/60 sm:text-sm"
            >
              {{ currentIndex + 1 }} / {{ availableImages.length }}
            </span>
          </div>

          <div
            class="flex shrink-0 items-center gap-1 rounded-xl bg-white/10 p-1"
            @pointerdown.stop
            @pointerup.stop
            @click.stop
          >
            <button
              type="button"
              class="flex size-9 cursor-pointer items-center justify-center rounded-lg transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-35"
              :disabled="zoom <= 1"
              :aria-label="$t('worker_profile.zoom_out')"
              @click="zoomOut"
            >
              <ZoomOut class="size-4" />
            </button>

            <button
              type="button"
              class="min-w-14 cursor-pointer rounded-lg px-2 py-2 text-sm font-semibold transition hover:bg-white/15"
              :aria-label="$t('worker_profile.reset_zoom')"
              @click="resetView"
            >
              {{ Math.round(zoom * 100) }}%
            </button>

            <button
              type="button"
              class="flex size-9 cursor-pointer items-center justify-center rounded-lg transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-35"
              :disabled="zoom >= 4"
              :aria-label="$t('worker_profile.zoom_in')"
              @click="zoomIn"
            >
              <ZoomIn class="size-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="availableImages.length > 1"
        class="service-details-thumbnails flex shrink-0 gap-2 overflow-x-auto border-t border-white/10 bg-black px-3 py-3 sm:px-4"
        :class="isFullscreen ? 'sm:justify-center' : ''"
      >
        <button
          v-for="(image, index) in availableImages"
          :key="image"
          type="button"
          class="relative h-16 w-20 shrink-0 cursor-pointer overflow-hidden rounded-lg border-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          :class="
            index === currentIndex
              ? 'border-white'
              : 'border-transparent opacity-65 hover:opacity-100'
          "
          :aria-label="`${alt} ${index + 1}`"
          @click="selectPhoto(index)"
        >
          <img
            :src="image"
            :alt="`${alt} ${index + 1}`"
            class="size-full object-cover"
            @error="markAsFailed(image)"
          />
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.details-slide-next-enter-active,
.details-slide-next-leave-active,
.details-slide-previous-enter-active,
.details-slide-previous-leave-active {
  transition: transform 300ms cubic-bezier(0.22, 1, 0.36, 1);
}

.details-slide-next-enter-from {
  transform: translateX(100%);
}

.details-slide-next-leave-to {
  transform: translateX(-100%);
}

.details-slide-previous-enter-from {
  transform: translateX(-100%);
}

.details-slide-previous-leave-to {
  transform: translateX(100%);
}

.details-slide-next-enter-to,
.details-slide-next-leave-from,
.details-slide-previous-enter-to,
.details-slide-previous-leave-from {
  transform: translateX(0);
}

.service-details-thumbnails {
  scrollbar-width: none;
}

.service-details-thumbnails::-webkit-scrollbar {
  display: none;
}

@media (prefers-reduced-motion: reduce) {
  .details-slide-next-enter-active,
  .details-slide-next-leave-active,
  .details-slide-previous-enter-active,
  .details-slide-previous-leave-active {
    transition-duration: 1ms;
  }
}
</style>
